import { oc } from "@orpc/contract";
import { z } from "zod";
import { OUTBOUND_EMAIL_STATUSES } from "@erp/shared-types";

const UUIDSchema = z.uuid();

/**
 * Outbound Email Status Schema
 */
export const OutboundEmailStatusSchema = z.enum(OUTBOUND_EMAIL_STATUSES);

export const OutboundEmailSchema = z.object({
  id: UUIDSchema,
  organizationId: UUIDSchema,
  senderUserId: UUIDSchema.nullable().optional(),
  topic: z.string(),
  from: z.string(),
  to: z.string(),
  cc: z.string().nullable().optional(),
  bcc: z.string().nullable().optional(),
  subject: z.string(),
  content: z.string(),
  htmlContent: z.string().nullable().optional(),
  status: OutboundEmailStatusSchema.optional(),
  sentAt: z.coerce.date().nullable().optional(),
  failureReason: z.string().nullable().optional(),
  messageId: z.string().nullable().optional(),
  // Thread tracking fields
  conversationId: z.string().nullable().optional(),
  inReplyToMessageId: z.string().nullable().optional(),
  isAiGenerated: z.boolean().optional(),
  sendingMailbox: z.string().nullable().optional(),
  // AI modification tracking fields
  aiOriginalContent: z.string().nullable().optional(),
  aiOriginalHtmlContent: z.string().nullable().optional(),
  modifiedByUserId: UUIDSchema.nullable().optional(),
  modifiedAt: z.coerce.date().nullable().optional(),
  createdAt: z.coerce.date(),
  updatedAt: z.coerce.date(),
  attachments: z
    .array(
      z.object({
        id: UUIDSchema,
        fileName: z.string(),
        sizeBytes: z.number(),
        mimeType: z.string().optional(),
        s3Url: z.string(),
      }),
    )
    .optional(),
});

/**
 * Get Outbound Email Input Schema
 */
export const GetOutboundEmailInputSchema = z.object({
  id: UUIDSchema,
});

/**
 * Send Email Input Schema
 */
export const SendEmailInputSchema = z.object({
  mailboxUpn: z.string().email().describe("The mailbox UPN to send from"),
  senderAddress: z
    .string()
    .email()
    .optional()
    .describe("Optional visible From address when sending as a group or alias"),
  to: z.array(z.string().email()).min(1).describe("Recipient email addresses"),
  cc: z.array(z.string().email()).optional().describe("CC email addresses"),
  bcc: z.array(z.string().email()).optional().describe("BCC email addresses"),
  subject: z.string().min(1).describe("Email subject"),
  htmlContent: z.string().min(1).describe("HTML content of the email"),
  isAiGenerated: z
    .boolean()
    .optional()
    .describe("Whether the content was AI-generated"),
  topic: z.string().optional().describe("Topic/category for the email"),
});

/**
 * Send Email Output Schema
 */
export const SendEmailOutputSchema = z.object({
  outboundEmailId: UUIDSchema,
  messageId: z.string().nullable(),
  conversationId: z.string().nullable(),
  status: OutboundEmailStatusSchema,
});

/**
 * Reply to Thread Input Schema
 */
export const ReplyToThreadInputSchema = z.object({
  mailboxUpn: z.string().email().describe("The mailbox UPN to send from"),
  senderAddress: z
    .string()
    .email()
    .optional()
    .describe(
      "Optional visible From address when replying as a group or alias",
    ),
  inReplyToMessageId: z
    .string()
    .min(1)
    .describe("The message ID of the email to reply to"),
  conversationId: z
    .string()
    .min(1)
    .describe("The conversation ID for thread tracking"),
  htmlContent: z.string().min(1).describe("HTML content of the reply"),
  replyAll: z
    .boolean()
    .optional()
    .describe("Whether to reply to all recipients"),
  isAiGenerated: z
    .boolean()
    .optional()
    .describe("Whether the content was AI-generated"),
  topic: z.string().optional().describe("Topic/category for the email"),
});

/**
 * Reply to Thread Output Schema
 */
export const ReplyToThreadOutputSchema = z.object({
  outboundEmailId: UUIDSchema,
  messageId: z.string().nullable(),
  conversationId: z.string().nullable(),
  status: OutboundEmailStatusSchema,
});

/**
 * Test Mailbox Input Schema
 */
export const TestMailboxInputSchema = z.object({
  mailboxUpn: z.string().email().describe("The mailbox UPN to test"),
  senderAddress: z
    .string()
    .email()
    .optional()
    .describe(
      "Optional visible From address when testing group or alias sending",
    ),
  recipientEmail: z.string().email().describe("Email address to send test to"),
});

/**
 * Test Mailbox Output Schema
 */
export const TestMailboxOutputSchema = z.object({
  success: z.boolean(),
  outboundEmailId: UUIDSchema.optional(),
  messageId: z.string().optional(),
  error: z.string().optional(),
});

/**
 * Outbound Email Contracts
 */
export const outboundEmailContracts = {
  // Get an outbound email by ID
  get: oc.input(GetOutboundEmailInputSchema).output(OutboundEmailSchema).route({
    method: "GET",
  }),

  // Send a new email via Microsoft Graph
  sendEmail: oc
    .input(SendEmailInputSchema)
    .output(SendEmailOutputSchema)
    .route({
      method: "POST",
      path: "/outbound-emails/send",
    }),

  // Reply to an existing email thread via Microsoft Graph
  replyToThread: oc
    .input(ReplyToThreadInputSchema)
    .output(ReplyToThreadOutputSchema)
    .route({
      method: "POST",
      path: "/outbound-emails/reply",
    }),

  // Send a test email to verify mailbox configuration
  testMailbox: oc
    .input(TestMailboxInputSchema)
    .output(TestMailboxOutputSchema)
    .route({
      method: "POST",
      path: "/outbound-emails/test-mailbox",
    }),
} as const;

// Export types
export type OutboundEmailStatus = z.infer<typeof OutboundEmailStatusSchema>;
export type OutboundEmail = z.infer<typeof OutboundEmailSchema>;
export type GetOutboundEmailInput = z.infer<typeof GetOutboundEmailInputSchema>;
export type SendEmailInput = z.infer<typeof SendEmailInputSchema>;
export type SendEmailOutput = z.infer<typeof SendEmailOutputSchema>;
export type ReplyToThreadInput = z.infer<typeof ReplyToThreadInputSchema>;
export type ReplyToThreadOutput = z.infer<typeof ReplyToThreadOutputSchema>;
export type TestMailboxInput = z.infer<typeof TestMailboxInputSchema>;
export type TestMailboxOutput = z.infer<typeof TestMailboxOutputSchema>;
